import { Components } from '@ionic/core';
import { IonLabel, IonList, IonText } from '@ionic/react';
import moment from 'moment';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Machine } from '../../api/graphql/generated';
import { MachineLogMessage } from '../../api/hooks/binding/binding.types';
import { useMonitorLogs } from '../../api/hooks/monitor-logs.hooks';
import { IonItem } from '../ionic/IonItem';
import { Highlighter } from './highlighter';

interface MachineDetailsProps {
  machine?: Machine;
}

interface LogMessageProps {
  log: MachineLogMessage;
  ionItemProps?: Partial<Components.IonItem>;
}

const MachineLogs = (props: MachineDetailsProps): ReactElement => {
  const { machine } = props;
  const { i18n } = useTranslation();

  const logs = useMonitorLogs(machine?.id, 1000);

  if (logs.length === 0) {
    return (
      <div className="full-height-width content-center">
        <IonLabel>{i18n.t('common:machine_logs_waiting_for_data')}</IonLabel>
      </div>
    );
  } else {
    return (
      <IonList lines="full">
        {logs.map((l) => {
          return <LogMessage key={l.messageId} log={l} />;
        })}
      </IonList>
    );
  }
};

const LogMessage = (props: LogMessageProps): ReactElement => {
  const { log, ionItemProps } = props;
  const color = log.level === 'error' ? 'danger' : undefined;
  const disableHighlight = Date.now() - log.timestamp > 10000;

  return (
    <Highlighter
      highlightOnMount={true}
      value={log.message}
      disabled={disableHighlight}
    >
      <IonItem
        className={'transparent-background'}
        lines="full"
        {...ionItemProps}
      >
        <IonText className="machine-logs">
          <b>{`[${moment(log.timestamp).format(
            'MMM D [at] h[:]mm[:]ss A'
          )}] `}</b>
          <IonText color={color}>{log.message}</IonText>
        </IonText>
      </IonItem>
    </Highlighter>
  );
};

export { MachineLogs, LogMessage };
